import { useState, useEffect, useMemo } from 'react';
import { bankingAPI, purchasePaymentsAPI } from '../../api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { FormLabel } from '../ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/Table';

/**
 * Bulk purchase payment modal for recording payments against one or more purchases.
 * - Single purchase: uses purchasePaymentsAPI.create
 * - Multiple purchases (same supplier): uses purchasePaymentsAPI.createBatch with allocations
 * - Mixed suppliers: blocked with validation message
 */
export const BulkPurchasePaymentModal = ({ purchases = [], onSuccess, onCancel }) => {
  const eligiblePurchases = useMemo(
    () => purchases.filter((p) => (p.balanceDue ?? 0) > 0 && p.status !== 'paid-in-full'),
    [purchases]
  );

  const supplierIds = useMemo(
    () => [...new Set(eligiblePurchases.map((p) => p.supplier?.id || p.supplierId).filter(Boolean))],
    [eligiblePurchases]
  );

  const isSingle = eligiblePurchases.length === 1;
  const hasMixedSuppliers = supplierIds.length > 1;
  const totalBalanceDue = useMemo(
    () => eligiblePurchases.reduce((sum, p) => sum + (p.balanceDue ?? 0), 0),
    [eligiblePurchases]
  );

  const [formData, setFormData] = useState({
    bank_account_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'manual',
    reference: '',
    notes: '',
  });

  const [allocations, setAllocations] = useState({});

  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    const initial = {};
    eligiblePurchases.forEach((p) => {
      const bal = p.balanceDue ?? 0;
      initial[p.id] = bal > 0 ? bal.toFixed(2) : '0.00';
    });
    setAllocations(initial);
  }, [eligiblePurchases]);

  const fetchBankAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const accounts = await bankingAPI.getAccounts({ include_inactive: false });
      const accountsArray = Array.isArray(accounts) ? accounts : [];
      setBankAccounts(accountsArray);
      if (accountsArray.length > 0) {
        setFormData((prev) => ({ ...prev, bank_account_id: accountsArray[0].id }));
      } else {
        setError('No bank accounts found. Please create a bank account first.');
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setError('Failed to load bank accounts. Please try again.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAllocationChange = (purchaseId, value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) {
      setAllocations((prev) => ({ ...prev, [purchaseId]: '' }));
      return;
    }
    setAllocations((prev) => ({ ...prev, [purchaseId]: value }));
  };

  const handlePayAllInFull = () => {
    const next = {};
    eligiblePurchases.forEach((p) => {
      const bal = p.balanceDue ?? 0;
      next[p.id] = bal > 0 ? bal.toFixed(2) : '0.00';
    });
    setAllocations(next);
  };

  const totalAllocated = useMemo(() => {
    return eligiblePurchases.reduce((sum, p) => {
      const val = parseFloat(allocations[p.id]);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [eligiblePurchases, allocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (eligiblePurchases.length === 0) {
      setError('No selected purchases have an outstanding balance.');
      return;
    }

    if (hasMixedSuppliers) {
      setError('All selected purchases must be from the same supplier. Please deselect or split manually.');
      return;
    }

    if (!formData.bank_account_id) {
      setError('Please select a bank account.');
      return;
    }

    if (isSingle) {
      const p = eligiblePurchases[0];
      const amount = parseFloat(allocations[p.id]);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid payment amount.');
        return;
      }
      if (amount > (p.balanceDue ?? 0)) {
        setError(
          `Payment amount cannot exceed balance due of ${formatCurrency(p.balanceDue)}`
        );
        return;
      }
    } else {
      let invalidAllocation = false;
      for (const p of eligiblePurchases) {
        const amt = parseFloat(allocations[p.id]);
        const bal = p.balanceDue ?? 0;
        if (isNaN(amt) || amt < 0) {
          setError(`Invalid amount for ${getReceiptNumber(p)}.`);
          invalidAllocation = true;
          break;
        }
        if (amt === 0) {
          setError(`Amount for ${getReceiptNumber(p)} must be greater than zero. Remove the purchase from selection if you do not want to pay it.`);
          invalidAllocation = true;
          break;
        }
        if (amt > bal) {
          setError(
            `Amount for ${getReceiptNumber(p)} (${formatCurrency(amt)}) exceeds balance due (${formatCurrency(bal)}).`
          );
          invalidAllocation = true;
          break;
        }
      }
      if (invalidAllocation) return;

      if (totalAllocated <= 0) {
        setError('Total allocation must be greater than zero.');
        return;
      }
    }

    setLoading(true);
    try {
      if (isSingle) {
        const p = eligiblePurchases[0];
        const amount = parseFloat(allocations[p.id]).toFixed(2);
        await purchasePaymentsAPI.create({
          purchase_id: p.id,
          bank_account_id: formData.bank_account_id,
          amount,
          payment_method: formData.payment_method || 'manual',
          payment_date: formData.payment_date || new Date().toISOString().split('T')[0],
          ...(formData.reference?.trim() && { reference: formData.reference.trim() }),
          ...(formData.notes?.trim() && { notes: formData.notes.trim() }),
        });
      } else {
        await purchasePaymentsAPI.createBatch({
          purchase_ids: eligiblePurchases.map((p) => p.id),
          allocations: eligiblePurchases.map((p) => ({
            purchase_id: p.id,
            amount: (parseFloat(allocations[p.id]) || 0).toFixed(2),
          })),
          bank_account_id: formData.bank_account_id,
          payment_method: formData.payment_method || 'manual',
          payment_date: formData.payment_date || new Date().toISOString().split('T')[0],
          ...(formData.reference?.trim() && { reference: formData.reference.trim() }),
          ...(formData.notes?.trim() && { notes: formData.notes.trim() }),
        });
      }
      onSuccess();
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to record payment.';
      if (errorData) {
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const first = errorData.errors[0];
          errorMessage = `${first.field || 'Field'}: ${first.message || errorData.error || 'Invalid value'}`;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Record payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getSupplierName = (p) => {
    return p.supplier?.supplierName ?? p.supplierName ?? '-';
  };

  const getReceiptNumber = (p) => {
    return p.receiptNumber ?? p.purchaseNum ?? '-';
  };

  if (eligiblePurchases.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            No selected purchases have an outstanding balance. Select purchases with a balance due to log payments.
          </p>
        </div>
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {hasMixedSuppliers && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            All selected purchases must be from the same supplier. Please deselect purchases from other suppliers.
          </p>
        </div>
      )}

      {/* Allocations table */}
      <div>
        <FormLabel>Selected purchases</FormLabel>
        <div className="rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow isHeader>
                <TableHead>Receipt No.</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Balance due</TableHead>
                <TableHead className="text-right w-32">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eligiblePurchases.map((p) => {
                const bal = p.balanceDue ?? 0;
                return (
                  <TableRow key={p.id}>
                    <TableCell className="text-gray-900 dark:text-gray-100">
                      {getReceiptNumber(p)}
                    </TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">
                      {getSupplierName(p)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-gray-900 dark:text-gray-100">
                      {formatCurrency(p.total)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {formatCurrency(bal)}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={allocations[p.id] ?? ''}
                        onChange={(e) => handleAllocationChange(p.id, e.target.value)}
                        min="0"
                        max={bal}
                        step="0.01"
                        placeholder="0.00"
                        className="h-8 text-right border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {eligiblePurchases.length > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={handlePayAllInFull}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium underline"
            >
              Pay all in full
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Total: {formatCurrency(totalAllocated)} / {formatCurrency(totalBalanceDue)}
            </span>
          </div>
        )}
      </div>

      {/* Bank account */}
      <div>
        <FormLabel required>Bank account</FormLabel>
        {loadingAccounts ? (
          <div className="h-8 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs">
            Loading bank accounts...
          </div>
        ) : bankAccounts.length === 0 ? (
          <div className="h-8 px-3 py-2 border border-red-300 dark:border-red-600 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs">
            No bank accounts available. Please create a bank account first.
          </div>
        ) : (
          <Select
            name="bank_account_id"
            value={formData.bank_account_id}
            onChange={handleFormChange}
            required
            className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select bank account</option>
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.account_name} {account.account_type && `(${account.account_type})`}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* Payment date */}
      <div>
        <FormLabel required>Payment date</FormLabel>
        <Input
          type="date"
          variant="borderless"
          name="payment_date"
          value={formData.payment_date}
          onChange={handleFormChange}
          className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Payment method */}
      <div>
        <FormLabel required>Payment method</FormLabel>
        <Select
          name="payment_method"
          value={formData.payment_method}
          onChange={handleFormChange}
          className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="manual">Manual</option>
          <option value="bank_transfer">Bank Transfer (BACS)</option>
          <option value="card">Credit/Debit Card</option>
          <option value="cash">Cash</option>
          <option value="cheque">Cheque</option>
          <option value="stripe">Stripe</option>
        </Select>
      </div>

      {/* Reference */}
      <div>
        <FormLabel>Reference</FormLabel>
        <Input
          type="text"
          name="reference"
          value={formData.reference}
          onChange={handleFormChange}
          placeholder="Payment reference number"
          className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Notes */}
      <div>
        <FormLabel>Notes</FormLabel>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleFormChange}
          rows={3}
          placeholder="Additional notes about this payment..."
          className="text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={loading || hasMixedSuppliers || loadingAccounts}
        >
          {loading ? 'Recording...' : 'Record payment'}
        </Button>
      </div>
    </form>
  );
};

export default BulkPurchasePaymentModal;
